import { Box, Spinner } from '@chakra-ui/react';
import type { EditorView } from '@codemirror/view';
import { useEffect, useRef, useState } from 'react';

import type { JsonLintIssue } from '../api/data-builder-json-lint';
import { resolveJsonPath } from './json-syntax-path';

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** Live, as-you-type structural checks. Returns issues keyed by JSON path. */
  validate?: (text: string) => JsonLintIssue[];
  /** Authoritative issues from the backend `/verify`, surfaced on demand. */
  serverDiagnostics?: JsonLintIssue[];
};

const EDITOR_HEIGHT = '320px';
const NO_DIAGNOSTICS: JsonLintIssue[] = [];

/**
 * CodeMirror JSON editor for the Data Builder "paste a request" flow: syntax
 * highlighting, inline lint squiggles (JSON syntax + client structural checks +
 * backend validation), line numbers, bracket matching, and code folding.
 *
 * CodeMirror and the JSON grammar are imported on demand inside the mount effect
 * so they stay out of the Data Builder bundle until an admin opens the paste
 * modal — mirroring how the app loads other heavy libraries only when needed.
 */
export const JsonRequestEditor = ({
  value,
  onChange,
  validate,
  serverDiagnostics = NO_DIAGNOSTICS,
}: Props) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  const initialValueRef = useRef(value);
  const validateRef = useRef(validate);
  const serverDiagnosticsRef = useRef(serverDiagnostics);
  const refreshDiagnosticsRef = useRef<(() => void) | null>(null);
  const [isReady, setIsReady] = useState(false);

  onChangeRef.current = onChange;
  validateRef.current = validate;
  serverDiagnosticsRef.current = serverDiagnostics;

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const [
        { EditorState },
        cmView,
        { basicSetup },
        { json, jsonParseLinter },
        { linter, lintGutter, forceLinting },
        { syntaxTree },
      ] = await Promise.all([
        import('@codemirror/state'),
        import('@codemirror/view'),
        import('codemirror'),
        import('@codemirror/lang-json'),
        import('@codemirror/lint'),
        import('@codemirror/language'),
      ]);

      if (cancelled || editorRef.current === null) {
        return;
      }

      const { EditorView } = cmView;

      const computeDiagnostics = (view: EditorView) => {
        const { state } = view;
        const issues = [
          ...(validateRef.current?.(state.doc.toString()) ?? []),
          ...serverDiagnosticsRef.current,
        ];
        return issues.flatMap((issue) => {
          const range = resolveJsonPath(
            syntaxTree(state).topNode,
            (from, to) => state.doc.sliceString(from, to),
            issue.path
          );
          return range === null
            ? []
            : [
                {
                  from: range.from,
                  to: range.to,
                  severity: issue.severity,
                  message: issue.message,
                },
              ];
        });
      };

      const view = new EditorView({
        parent: editorRef.current,
        state: EditorState.create({
          doc: initialValueRef.current,
          extensions: [
            basicSetup,
            json(),
            linter(jsonParseLinter()),
            linter(computeDiagnostics),
            lintGutter(),
            EditorView.lineWrapping,
            EditorView.theme({
              '&': { height: '100%' },
              '.cm-scroller': {
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '13px',
              },
              '.cm-content': { padding: '8px' },
            }),
            EditorView.updateListener.of((update) => {
              if (update.docChanged) {
                // Server diagnostics pin to the verified text; an edit makes
                // them stale, so drop them until the next verify.
                serverDiagnosticsRef.current = NO_DIAGNOSTICS;
                onChangeRef.current(update.state.doc.toString());
              }
            }),
          ],
        }),
      });

      viewRef.current = view;
      refreshDiagnosticsRef.current = () => forceLinting(view);
      view.focus();
      setIsReady(true);
    })();

    return () => {
      cancelled = true;
      refreshDiagnosticsRef.current = null;
      viewRef.current?.destroy();
      viewRef.current = null;
    };
  }, []);

  // Re-run the linter when backend validation results change so the inline
  // marks update without waiting for the next edit. The effect body reads the
  // synced ref rather than the prop, so the dependency is intentional.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire on diagnostics change
  useEffect(() => {
    refreshDiagnosticsRef.current?.();
  }, [serverDiagnostics]);

  // Reconcile external value changes (e.g. the Format button rewrites the JSON).
  // Comparing against the live doc means ordinary keystrokes — which echo back
  // through `value` unchanged — never trigger a redundant dispatch or cursor jump.
  useEffect(() => {
    const view = viewRef.current;
    if (view === null) {
      return;
    }

    const currentDoc = view.state.doc.toString();
    if (value !== currentDoc) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: value },
      });
    }
  }, [value]);

  return (
    <Box
      position="relative"
      height={EDITOR_HEIGHT}
      borderWidth="1px"
      borderRadius="md"
      overflow="hidden"
    >
      <Box
        ref={editorRef}
        height="100%"
        overflow="auto"
        data-testid="saved-datasets-paste-editor"
      />
      {isReady ? null : (
        <Box
          position="absolute"
          inset="0"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner size="sm" />
        </Box>
      )}
    </Box>
  );
};
